import type { StrategiesSettingsModel } from '@/shared/generated'

import { useMemo } from 'react'
import { find, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	getEnumOptions,
	getResolvedSchema,
	translateOptions,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export function StrategyView({ data }: { data?: StrategiesSettingsModel }) {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:strategy'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const isLoading = schemaQuery.isLoading
	const { demand_grow, demand_maintain, markdown_logic } = useMemo(() => {
		const growOptions = translateOptions(
			getEnumOptions(
				prop('properties.demand_strategy.properties.target', schemaQuery.data)
			),
			t
		)

		const markdownOptions = translateOptions(
			getEnumOptions(
				prop('properties.markdown_strategy.properties.target', schemaQuery.data)
			),
			t
		)

		const maintainOptions = translateOptions(
			getEnumOptions(
				prop('properties.demand_strategy.properties.protect', schemaQuery.data)
			),
			t
		)

		return {
			demand_grow: find({ value: data?.demand_strategy.target }, growOptions),
			demand_maintain: find(
				{
					value: data?.demand_strategy.protect,
				},
				maintainOptions
			),
			markdown_logic: find(
				{
					value: data?.markdown_strategy.target,
				},
				markdownOptions
			),
		}
	}, [schemaQuery.data, data, t])

	return (
		<DescriptionList>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('og_metric_to_grow', { defaultValue: 'Grow' })}
				</DescriptionTerm>
				<DescriptionDetail>
					{isLoading ? <Spinner /> : <strong>{demand_grow?.label}</strong>}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('og_metric_to_maintain', { defaultValue: 'Maintain' })}
				</DescriptionTerm>
				<DescriptionDetail>
					{isLoading ? <Spinner /> : <strong>{demand_maintain?.label}</strong>}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('og_metric_markdown', { defaultValue: 'Markdown logic' })}
				</DescriptionTerm>
				<DescriptionDetail>
					{isLoading ? <Spinner /> : <strong>{markdown_logic?.label}</strong>}
				</DescriptionDetail>
			</DescriptionGroup>
		</DescriptionList>
	)
}
